import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { compressImage } from "@/utils/compressImage";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import DoctorReview from "@/components/DoctorReview";
import { ImageLightbox } from "@/components/ImageLightbox";
import {
  Upload, Eye, Search, Loader2, AlertTriangle,
  CheckCircle, Clock, FileImage, User, ArrowLeft, Trash2, Mail, X, Printer, Download
} from "lucide-react";
import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

export default function RetinalAnalysisPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const API_URL = API;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const authFetch = useCallback((url, options = {}) => {
    const token = localStorage.getItem("session_token");
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  }, []);

  // State
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Upload form
  const [patientFirstName, setPatientFirstName] = useState("");
  const [patientLastName, setPatientLastName] = useState("");
  const [patientId, setPatientId] = useState("");
  const [selectedEye, setSelectedEye] = useState("right");
  const [bwFile, setBwFile] = useState(null);
  const [colorFile, setColorFile] = useState(null);
  const [bwPreview, setBwPreview] = useState(null);
  const [colorPreview, setColorPreview] = useState(null);
  const [sendingToDoctor, setSendingToDoctor] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImagePath, setLightboxImagePath] = useState(null);

  const fetchAnalyses = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/retinal/analyses`);
      if (response.ok) {
        const data = await response.json();
        setAnalyses(data);
      }
    } catch (error) {
      console.error("Failed to fetch analyses:", error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, authFetch]);

  useEffect(() => {
    fetchAnalyses();
  }, [fetchAnalyses]); // eslint-disable-line

  const handleBwChange = (e) => {
    const file = e.target.files[0];
    if (file) { setBwFile(file); setBwPreview(URL.createObjectURL(file)); }
  };
  const handleColorChange = (e) => {
    const file = e.target.files[0];
    if (file) { setColorFile(file); setColorPreview(URL.createObjectURL(file)); }
  };

  const handleUploadAndAnalyze = async (e) => {
    e.preventDefault();
    if ((!bwFile && !colorFile) || !patientFirstName.trim() || !patientLastName.trim() || !patientId) {
      toast.error("Palun täitke kõik väljad ja valige vähemalt üks pilt");
      return;
    }
    if (!patientId.match(/^\d{11}$/)) {
      toast.error("Isikukood peab olema 11-kohaline number");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      if (bwFile) formData.append("bw_image", await compressImage(bwFile));
      if (colorFile) formData.append("color_image", await compressImage(colorFile));
      formData.append("patient_first_name", patientFirstName.trim());
      formData.append("patient_last_name", patientLastName.trim());
      formData.append("patient_id", patientId);
      formData.append("eye", selectedEye);

      const uploadRes = await authFetch(`${API_URL}/retinal/upload`, {
        method: "POST",
        body: formData,
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json();
        toast.error(err.detail || "Üleslaadimine ebaõnnestus");
        setUploading(false);
        return;
      }

      const uploadData = await uploadRes.json();
      toast.success("Pilt üles laetud, alustan analüüsi...");
      setUploading(false);
      setAnalyzing(uploadData.analysis_id);

      // Analyze
      const analyzeRes = await authFetch(`${API_URL}/retinal/analyze/${uploadData.analysis_id}`, {
        method: "POST",
      });

      if (analyzeRes.ok) {
        toast.success("Analüüs valmis!");
        // Fetch the full analysis record (includes image_path, patient info, etc.)
        const fullRes = await authFetch(`${API_URL}/retinal/analyses/${uploadData.analysis_id}`);
        if (fullRes.ok) {
          const fullData = await fullRes.json();
          if (fullData.bw_image_path) loadImage(fullData.bw_image_path);
          if (fullData.color_image_path) loadImage(fullData.color_image_path);
          if (fullData.image_path) loadImage(fullData.image_path);
          setSelectedAnalysis(fullData);
        }
      } else {
        toast.error("Analüüs ebaõnnestus");
      }

      // Keep patient name and ID, clear files, toggle eye for next upload
      setBwFile(null); setColorFile(null);
      setBwPreview(null); setColorPreview(null);
      setSelectedEye(prev => prev === "right" ? "left" : "right");
      fetchAnalyses();
    } catch (error) {
      console.error("Retinal analysis error:", error);
      toast.error("Viga tekkis: " + (error.message || "Tundmatu viga"));
    } finally {
      setUploading(false);
      setAnalyzing(null);
    }
  };

  const viewAnalysis = async (analysisId) => {
    try {
      const res = await authFetch(`${API_URL}/retinal/analyses/${analysisId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedAnalysis(data);
        if (data.bw_image_path) loadImage(data.bw_image_path);
        if (data.color_image_path) loadImage(data.color_image_path);
        if (data.image_path) loadImage(data.image_path);
      }
    } catch (error) {
      toast.error("Analüüsi laadimine ebaõnnestus");
    }
  };

  const handlePdf = async (action) => {
    if (!selectedAnalysis) return;
    try {
      toast.info(action === "print" ? "Genereerin..." : "Laen alla...");
      const res = await authFetch(`${API_URL}/retinal/analyses/${selectedAnalysis.analysis_id}/pdf`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (action === "print") {
        const w = window.open(url);
        if (w) w.onload = () => w.print();
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Silmapohja_raport_${selectedAnalysis.analysis_id.slice(-6)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error("PDF genereerimine ebaõnnestus");
    }
  };

  const sendToDoctor = async () => {
    if (!selectedAnalysis) return;
    setSendingToDoctor(true);
    try {
      const res = await authFetch(`${API_URL}/retinal/${selectedAnalysis.analysis_id}/notify-doctor`, { method: "POST" });
      if (res.ok) {
        toast.success("E-post saadetud arstile");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Saatmine ebaõnnestus");
      }
    } catch (error) {
      toast.error("E-posti saatmine ebaõnnestus");
    } finally {
      setSendingToDoctor(false);
    }
  };

  const deleteAnalysis = async (analysisId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Kas olete kindel, et soovite selle analüüsi kustutada?")) return;
    try {
      const res = await authFetch(`${API_URL}/retinal/analyses/${analysisId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Analüüs kustutatud");
        if (selectedAnalysis?.analysis_id === analysisId) setSelectedAnalysis(null);
        setAnalyses(prev => prev.filter(a => a.analysis_id !== analysisId));
      } else {
        toast.error("Kustutamine ebaõnnestus");
      }
    } catch (error) {
      toast.error("Kustutamine ebaõnnestus");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle className="w-3 h-3 mr-1" />Valmis</Badge>;
      case "analyzing":
        return <Badge className="bg-blue-100 text-blue-700 border-0"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Analüüsib</Badge>;
      case "failed":
        return <Badge className="bg-red-100 text-red-700 border-0"><AlertTriangle className="w-3 h-3 mr-1" />Ebaõnnestunud</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700 border-0"><Clock className="w-3 h-3 mr-1" />Üles laetud</Badge>;
    }
  };

  const getImageUrl = (path) => {
    return `${API_URL}/retinal/image/${path}`;
  };

  // Load image via authFetch with proper Authorization header
  const imageBlobUrlsRef = useRef({});
  const [imageBlobUrls, setImageBlobUrls] = useState({});

  const loadImage = useCallback(async (imagePath) => {
    if (!imagePath || imageBlobUrlsRef.current[imagePath]) return;
    imageBlobUrlsRef.current[imagePath] = "loading";
    try {
      const response = await authFetch(getImageUrl(imagePath));
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        imageBlobUrlsRef.current[imagePath] = url;
        setImageBlobUrls(prev => ({ ...prev, [imagePath]: url }));
      }
    } catch (error) {
      console.error("Image load failed:", error);
    }
  }, [authFetch]);

  const getImageSrc = (imagePath) => {
    return imageBlobUrls[imagePath] || null;
  };

  // Pre-load thumbnail images only for first visible items, rest loaded lazily
  useEffect(() => {
    if (!analyses.length) return;
    const firstBatch = analyses.slice(0, 6);
    firstBatch.forEach(a => {
      if (a.bw_image_path) loadImage(a.bw_image_path);
      if (a.color_image_path) loadImage(a.color_image_path);
      if (!a.bw_image_path && !a.color_image_path && a.image_path) loadImage(a.image_path);
    });
  }, [analyses, loadImage]);

  // Lazy load image when analysis comes into view or is hovered
  const lazyLoadAnalysisImages = useCallback((a) => {
    if (a.bw_image_path && !imageBlobUrlsRef.current[a.bw_image_path]) loadImage(a.bw_image_path);
    if (a.color_image_path && !imageBlobUrlsRef.current[a.color_image_path]) loadImage(a.color_image_path);
    if (a.image_path && !imageBlobUrlsRef.current[a.image_path]) loadImage(a.image_path);
  }, [loadImage]);

  const eyeLabel = (eye) => {
    if (eye === "right") return "OD (parem)";
    if (eye === "left") return "OS (vasak)";
    return "";
  };

  const filteredAnalyses = analyses.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    // Search by patient_id (includes match)
    if (a.patient_id.includes(searchQuery)) return true;
    // Search by name: check if first or last name STARTS WITH query
    const nameParts = a.patient_name.toLowerCase().split(" ");
    return nameParts.some(part => part.startsWith(q));
  });

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="retinal-analysis-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="md:ml-64 min-h-screen">
        <header className="bg-white border-b border-slate-200 px-4 py-4">
          <div className="max-w-6xl mx-auto flex items-center gap-3">
            <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Silmapõhja analüüs</h1>
              <p className="text-xs text-slate-500">AI-põhine retinopaatia tuvastamine</p>
            </div>
          </div>
        </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6">
        {/* If viewing specific analysis */}
        {selectedAnalysis ? (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setSelectedAnalysis(null)} data-testid="back-to-list">
              <ArrowLeft className="w-4 h-4 mr-2" /> Tagasi nimekirja
            </Button>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Images */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Silmapõhja pildid</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteAnalysis(selectedAnalysis.analysis_id)}
                      data-testid="delete-analysis-detail-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Kustuta
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {/* B&W image */}
                    {selectedAnalysis.bw_image_path && getImageSrc(selectedAnalysis.bw_image_path) && (
                      <div>
                        <p className="text-xs text-slate-400 mb-1">Must-valge</p>
                        <img
                          src={getImageSrc(selectedAnalysis.bw_image_path)}
                          alt="Must-valge"
                          className="w-full rounded-lg border border-slate-200 cursor-zoom-in"
                          data-testid="retinal-bw-view"
                          onClick={() => { setLightboxImagePath(selectedAnalysis.bw_image_path); setLightboxOpen(true); }}
                        />
                      </div>
                    )}
                    {/* Color image */}
                    {selectedAnalysis.color_image_path && getImageSrc(selectedAnalysis.color_image_path) && (
                      <div>
                        <p className="text-xs text-slate-400 mb-1">Värviline</p>
                        <img
                          src={getImageSrc(selectedAnalysis.color_image_path)}
                          alt="Värviline"
                          className="w-full rounded-lg border border-slate-200 cursor-zoom-in"
                          data-testid="retinal-color-view"
                          onClick={() => { setLightboxImagePath(selectedAnalysis.color_image_path); setLightboxOpen(true); }}
                        />
                      </div>
                    )}
                    {/* Legacy single image */}
                    {!selectedAnalysis.bw_image_path && !selectedAnalysis.color_image_path && selectedAnalysis.image_path && getImageSrc(selectedAnalysis.image_path) && (
                      <div className="col-span-2">
                        <img
                          src={getImageSrc(selectedAnalysis.image_path)}
                          alt="Silmapõhja pilt"
                          className="w-full rounded-lg border border-slate-200 cursor-zoom-in"
                          data-testid="retinal-image-view"
                          onClick={() => { setLightboxImagePath(selectedAnalysis.image_path); setLightboxOpen(true); }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-slate-600">
                    <p><span className="text-slate-400">Patsient:</span> {selectedAnalysis.patient_name}</p>
                    <p><span className="text-slate-400">Isikukood:</span> {selectedAnalysis.patient_id}</p>
                    {selectedAnalysis.eye && selectedAnalysis.eye !== "unknown" && (
                      <p><span className="text-slate-400">Silm:</span> {eyeLabel(selectedAnalysis.eye)}</p>
                    )}
                    {selectedAnalysis.analyzed_by_email && (
                      <p><span className="text-slate-400">Analüüsija:</span> {selectedAnalysis.analyzed_by_email}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Analysis Result */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">AI analüüsi tulemus</CardTitle>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handlePdf("print")} data-testid="retinal-print-btn" title="Prindi">
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handlePdf("download")} data-testid="retinal-download-btn" title="Lae alla">
                        <Download className="w-4 h-4" />
                      </Button>
                      {getStatusBadge(selectedAnalysis.status)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {selectedAnalysis.result_hidden ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center" data-testid="result-hidden-notice">
                      <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="font-semibold text-amber-800">Analüüs teostatud</p>
                      <p className="text-sm text-amber-600 mt-1">Ootab arsti kontrolli. Tulemus muutub nähtavaks pärast arsti kinnitust.</p>
                    </div>
                  ) : selectedAnalysis.analysis_result ? (
                    <div className="prose prose-sm max-w-none text-slate-700" data-testid="analysis-result-text">
                      <div className="whitespace-pre-wrap text-sm leading-relaxed">
                        {selectedAnalysis.analysis_result}
                      </div>
                      <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700" data-testid="ai-disclaimer">
                        <strong>NB:</strong> See on tehisintellekti (Gemini 3.1 Pro Vision) esialgne hinnang. Lõpliku otsuse teeb arst. Andmeid säilitatakse 10 aastat vastavalt Eesti tervishoiuteenuste korraldamise seadusele (§ 769).
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">Analüüs pole veel tehtud või ebaõnnestus</p>
                  )}
                  {!selectedAnalysis.result_hidden && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs text-amber-700">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        See on AI-põhine sõeluuring. Lõpliku diagnoosi peab tegema kvalifitseeritud silmaarst.
                      </p>
                    </div>
                  )}
                  {selectedAnalysis.status === "completed" && !selectedAnalysis.result_hidden && (
                    <DoctorReview
                      reviewUrl={`/retinal/analyses/${selectedAnalysis.analysis_id}/doctor-review`}
                      sendUrl={`/retinal/analyses/${selectedAnalysis.analysis_id}/send-review-to-nurse`}
                      record={selectedAnalysis}
                      onUpdate={() => { fetchAnalyses(); viewAnalysis(selectedAnalysis.analysis_id); }}
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-6">
            {/* Upload Form */}
            <Card className="border-slate-200 h-fit" data-testid="upload-form-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Upload className="w-4 h-4" /> Uus analüüs
                  </CardTitle>
                  {(patientFirstName || patientLastName) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPatientFirstName("");
                        setPatientLastName("");
                        setPatientId("");
                        setBwFile(null);
                        setBwPreview(null);
                        setColorFile(null);
                        setColorPreview(null);
                        setSelectedEye("right");
                      }}
                      className="text-xs text-slate-500"
                      data-testid="new-patient-btn"
                    >
                      <User className="w-3 h-3 mr-1" /> Uus patsient
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUploadAndAnalyze} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Eesnimi *</Label>
                      <Input
                        value={patientFirstName}
                        onChange={(e) => setPatientFirstName(e.target.value)}
                        placeholder="Eesnimi"
                        required
                        data-testid="retinal-patient-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Perekonnanimi *</Label>
                      <Input
                        value={patientLastName}
                        onChange={(e) => setPatientLastName(e.target.value)}
                        placeholder="Perekonnanimi"
                        required
                        data-testid="retinal-patient-last-name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Isikukood *</Label>
                    <Input
                      value={patientId}
                      onChange={(e) => setPatientId(e.target.value.replace(/\D/g, "").slice(0, 11))}
                      placeholder="39901011234"
                      maxLength={11}
                      required
                      data-testid="retinal-patient-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Silm *</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedEye("right")}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          selectedEye === "right"
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                        data-testid="eye-right-btn"
                      >
                        Parem silm (OD)
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedEye("left")}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          selectedEye === "left"
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                        data-testid="eye-left-btn"
                      >
                        Vasak silm (OS)
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Must-valge pilt (red-free) *</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleBwChange}
                      className="cursor-pointer"
                      data-testid="retinal-bw-input"
                    />
                    {bwPreview && (
                      <img src={bwPreview} alt="MV eelvaade" className="w-full h-36 object-cover rounded-lg border border-slate-200 mt-1" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Värviline pilt *</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleColorChange}
                      className="cursor-pointer"
                      data-testid="retinal-color-input"
                    />
                    {colorPreview && (
                      <img src={colorPreview} alt="Värviline eelvaade" className="w-full h-36 object-cover rounded-lg border border-slate-200 mt-1" />
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    disabled={uploading || analyzing}
                    data-testid="retinal-analyze-btn"
                  >
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Laen üles...</>
                    ) : analyzing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analüüsin...</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" /> Laadi üles ja analüüsi</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Analysis History */}
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Otsi patsiendi nime või isikukoodi järgi..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="retinal-search"
                />
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : filteredAnalyses.length === 0 ? (
                <Card className="border-slate-200">
                  <CardContent className="p-8 text-center">
                    <FileImage className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">
                      {searchQuery ? "Analüüse ei leitud" : "Analüüse pole veel tehtud"}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {filteredAnalyses.map(a => {
                    const needsReview = a.status === "completed" && !a.doctor_reviewed;
                    return (
                    <Card
                      key={a.analysis_id}
                      className={`group cursor-pointer hover:shadow-md transition-shadow ${needsReview ? "border-red-300 bg-red-50/30" : "border-slate-200"}`}
                      onClick={() => viewAnalysis(a.analysis_id)}
                      onMouseEnter={() => lazyLoadAnalysisImages(a)}
                      onTouchStart={() => lazyLoadAnalysisImages(a)}
                      data-testid={`analysis-card-${a.analysis_id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          {/* Thumbnail */}
                          <div className="relative flex-shrink-0">
                            {needsReview && (
                              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 animate-pulse z-10 ring-2 ring-white" title="Ootab arsti kontrolli" data-testid="nurse-upload-dot" />
                            )}
                            <div className="flex gap-0.5">
                              {a.bw_image_path && getImageSrc(a.bw_image_path) ? (
                                <img
                                  src={getImageSrc(a.bw_image_path)}
                                  alt="MV"
                                  className={`w-8 h-16 rounded-l-lg object-cover bg-slate-100 ${needsReview ? "border-2 border-red-400" : "border border-slate-200"}`}
                                  loading="lazy"
                                />
                              ) : a.bw_image_path ? (
                                <div className={`w-8 h-16 rounded-l-lg bg-slate-100 animate-pulse ${needsReview ? "border-2 border-red-400" : "border border-slate-200"}`} />
                              ) : null}
                              {a.color_image_path && getImageSrc(a.color_image_path) ? (
                                <img
                                  src={getImageSrc(a.color_image_path)}
                                  alt="Värv"
                                  className={`w-8 h-16 rounded-r-lg object-cover bg-slate-100 ${needsReview ? "border-2 border-red-400" : "border border-slate-200"}`}
                                  loading="lazy"
                                  data-testid={`thumb-${a.analysis_id}`}
                                />
                              ) : a.color_image_path ? (
                                <div className={`w-8 h-16 rounded-r-lg bg-slate-100 animate-pulse ${needsReview ? "border-2 border-red-400" : "border border-slate-200"}`} />
                              ) : null}
                              {!a.bw_image_path && !a.color_image_path && a.image_path && getImageSrc(a.image_path) ? (
                                <img
                                  src={getImageSrc(a.image_path)}
                                  alt=""
                                  className={`w-16 h-16 rounded-lg object-cover bg-slate-100 ${needsReview ? "border-2 border-red-400" : "border border-slate-200"}`}
                                  loading="lazy"
                                  data-testid={`thumb-${a.analysis_id}`}
                                />
                              ) : !a.bw_image_path && !a.color_image_path && a.image_path ? (
                                <div className={`w-16 h-16 rounded-lg bg-slate-100 animate-pulse ${needsReview ? "border-2 border-red-400" : "border border-slate-200"}`} />
                              ) : null}
                              {!a.bw_image_path && !a.color_image_path && !a.image_path && (
                                <div className={`w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center ${needsReview ? "border-2 border-red-400" : "border border-slate-200"}`}>
                                  <FileImage className="w-6 h-6 text-slate-300" />
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {a.patient_name}
                              {a.eye && a.eye !== "unknown" && (
                                <span className="ml-2 text-xs font-normal text-slate-500">({eyeLabel(a.eye)})</span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500">IK: {a.patient_id}</p>
                            {a.analyzed_by_email && (
                              <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1 truncate">
                                <User className="w-3 h-3 flex-shrink-0" /> {a.analyzed_by_email}
                              </p>
                            )}
                          </div>
                          {/* Status */}
                          <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                            {needsReview ? (
                              <Badge className="bg-red-100 text-red-700 text-xs" data-testid="needs-review-badge">Kontrollimata</Badge>
                            ) : a.doctor_reviewed ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs" data-testid="reviewed-badge">Kontrollitud</Badge>
                            ) : (
                              getStatusBadge(a.status)
                            )}
                            <p className="text-xs text-slate-400">
                              {new Date(a.created_at).toLocaleDateString("et-EE")}
                            </p>
                            <button
                              onClick={(e) => deleteAnalysis(a.analysis_id, e)}
                              className="p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`delete-analysis-${a.analysis_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      </div>
      {/* Image Lightbox */}
      {lightboxOpen && lightboxImagePath && getImageSrc(lightboxImagePath) && (
        <ImageLightbox
          src={getImageSrc(lightboxImagePath)}
          alt={`${selectedAnalysis?.patient_name || ''} - Silmapõhja pilt`}
          onClose={() => { setLightboxOpen(false); setLightboxImagePath(null); }}
        />
      )}

      <BottomNav />
    </div>
  );
}
